import { ArrowSquareOut, CaretLeft, CaretRight, Warning } from "@phosphor-icons/react"
import { type ColDef, type GridOptions, type ICellRendererParams } from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import { useCallback, useMemo, useRef, useState } from "react"
import { useRouter } from "@tanstack/react-router"
import { NEEDS_ATTENTION_TABLE_ID } from "~/constants"

import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "../reusable/Dialog"
import { AlertWarning } from "../reusable/Alert"
import { Button } from "../reusable/Button"
import { api } from "~/utils/api"
import { formatName } from "~/utils/namingUtils"
import AreaGraphSilhouette from "../reusable/skeletonSilhouette/AreaGraphSilhouette"
import { CellProductImage } from "../planning/ProductTable/ProductCellRenderers"
import { formatVariantId } from "~/utils/helpers"
import { DataTableBaseV2 } from "../reusable/DataTableV2"

const PAGE_SIZE = 25

interface InvalidPreOrderRow {
  id: string
  path: string[]
  name: string
  productName: string | null
  imageUrl: string | null
  preOrderValue: number
  knownPreOrder: number
  invalidQty: number
  isVariant: boolean
}

function VariantCellRenderer(params: ICellRendererParams<InvalidPreOrderRow>) {
  const data = params.node.data ?? params.node.allLeafChildren?.[0]?.data
  if (!data) return null

  if (data.isVariant) {
    const onNavigate = params.context?.onNavigateToVariant as ((name: string) => void) | undefined
    const variantName = data.name
    const productName = data.productName ?? "Unknown product"
    const showBothNames = !variantName.includes(productName) && productName !== "Unknown product"
    const title = showBothNames ? `${productName} / ${variantName}` : variantName

    return (
      <div className="flex items-center gap-3">
        <CellProductImage imageUrl={data.imageUrl ?? undefined} />
        <div className="leading-5 min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{title}</div>
          <div className="text-fg-muted text-xs truncate">#{formatVariantId(data.id)}</div>
        </div>
        {onNavigate && (
          <button
            className="text-fg-muted hover:text-fg-base shrink-0"
            title="View in Needs Attention"
            onClick={(e) => {
              e.stopPropagation()
              onNavigate(title)
            }}
          >
            <ArrowSquareOut size={16} />
          </button>
        )}
      </div>
    )
  }

  return <span className="text-fg-subtle">{data.name}</span>
}

function flattenRows(
  rows: Array<{
    variantId: string
    variantName: string | null
    productName: string | null
    imageUrl: string | null
    totalPreOrderValue: number
    totalKnownPreOrder: number
    totalInvalidQty: number
    skus: Array<{
      skuId: string
      skuName: string | null
      preOrderValue: number
      knownPreOrder: number
      invalidQty: number
    }>
  }>,
): InvalidPreOrderRow[] {
  const result: InvalidPreOrderRow[] = []
  for (const row of rows) {
    result.push({
      id: row.variantId,
      path: [row.variantId],
      name: row.variantName ?? row.variantId,
      productName: row.productName,
      imageUrl: row.imageUrl,
      preOrderValue: row.totalPreOrderValue,
      knownPreOrder: row.totalKnownPreOrder,
      invalidQty: row.totalInvalidQty,
      isVariant: true,
    })
    for (const sku of row.skus) {
      result.push({
        id: sku.skuId,
        path: [row.variantId, sku.skuId],
        name: sku.skuName ?? sku.skuId,
        productName: null,
        imageUrl: null,
        preOrderValue: sku.preOrderValue,
        knownPreOrder: sku.knownPreOrder,
        invalidQty: sku.invalidQty,
        isVariant: false,
      })
    }
  }
  return result
}

const preorderLabel = formatName("preorder", true, true)

const columnDefs: ColDef<InvalidPreOrderRow>[] = [
  {
    colId: "preOrderValue",
    headerName: preorderLabel,
    field: "preOrderValue",
    type: "rightAligned",
    minWidth: 120,
  },
  {
    colId: "knownPreOrder",
    headerName: "In Demand",
    field: "knownPreOrder",
    type: "rightAligned",
    minWidth: 120,
  },
  {
    colId: "invalidQty",
    headerName: "Outside Collection",
    field: "invalidQty",
    type: "rightAligned",
    minWidth: 150,
    cellRenderer: (params: ICellRendererParams<InvalidPreOrderRow>) => {
      const value = params.value as number | undefined
      if (value == null) return null
      return (
        <div className="flex items-center justify-end gap-1.5">
          <Warning
            className="shrink-0 !fill-indicator-checkbox-red text-white"
            weight="fill"
            size={16}
          />
          <span>{value}</span>
        </div>
      )
    },
    cellStyle: (params) => ({
      color: "var(--color-badge-red-fg-text)",
      fontWeight: params.node.data?.isVariant ? 500 : 400,
    }),
  },
]

const autoGroupColumnDef: ColDef<InvalidPreOrderRow> = {
  headerName: "Variant",
  minWidth: 300,
  flex: 1,
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: VariantCellRenderer,
  },
}

const baseGridOptions: GridOptions<InvalidPreOrderRow> = {
  treeData: true,
  getDataPath: (data) => data.path,
  groupDefaultExpanded: 0,
  autoSizeStrategy: { type: "fitGridWidth" },
}

const defaultColDef: ColDef = {
  suppressHeaderMenuButton: true,
  sortable: false,
  minWidth: 100,
}

interface Props {
  open: boolean
  onClose: () => void
  variantIds: string[]
  warehouseId?: string
  channel?: string
  startDate: Date
  endDate: Date
}

export const InvalidPreOrdersModal = ({
  open,
  onClose,
  variantIds,
  warehouseId,
  channel,
  startDate,
  endDate,
}: Props) => {
  const router = useRouter()
  const [page, setPage] = useState(0)
  const gridRef = useRef<AgGridReact<InvalidPreOrderRow>>(null)

  const handleNavigateToVariant = useCallback(
    (variantName: string) => {
      sessionStorage.setItem(
        NEEDS_ATTENTION_TABLE_ID + "npi-table-search-text",
        JSON.stringify(variantName),
      )
      router.navigate({ to: "/product-management" })
      onClose()
    },
    [router, onClose],
  )

  const gridOptions = useMemo<GridOptions<InvalidPreOrderRow>>(
    () => ({
      ...baseGridOptions,
      context: { onNavigateToVariant: handleNavigateToVariant },
    }),
    [handleNavigateToVariant],
  )

  const { data, isLoading } = api.preOrder.getInvalidPreOrderDetails.useQuery(
    {
      variantIds: variantIds.length > 0 ? variantIds : undefined,
      warehouseId,
      channel,
      startDate,
      endDate,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { enabled: open },
  )

  const rowData = useMemo(() => (data ? flattenRows(data.rows) : []), [data])

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0
  const showingFrom = data && data.total > 0 ? page * PAGE_SIZE + 1 : 0
  const showingTo = data ? Math.min((page + 1) * PAGE_SIZE, data.total) : 0

  return (
    <DialogRoot open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContentV2 className="max-w-5xl max-h-[80vh] flex flex-col">
        <DialogHeaderV2>
          <DialogTitle>{formatName("preorder", true, true)} requiring review</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2 className="overflow-auto flex-1">
          <AlertWarning
            className="mb-4"
            title={`Resolve ${formatName("preorder", true)} outside of a collection`}
          >
            These {formatName("preorder", true)} are placed on variants that are not part of an
            active collection or demand. Review whether each variant should be activated in a
            collection or if the {formatName("preorder", true)} should be cancelled in your ERP.
          </AlertWarning>
          {isLoading ? (
            <AreaGraphSilhouette />
          ) : !data || data.rows.length === 0 ? (
            <p className="text-fg-muted text-center py-8">
              No invalid {formatName("preorder", true)} found.
            </p>
          ) : (
            <div className="h-[500px] w-full">
              <DataTableBaseV2<InvalidPreOrderRow>
                id="invalid-preorders"
                gridRef={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColumnDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                gridOptions={gridOptions}
                rowIdField="id"
                showPagination={false}
                emptyMessage={`No invalid ${formatName("preorder", true)} found.`}
              />
            </div>
          )}
        </DialogBodyV2>
        {data && data.total > PAGE_SIZE && (
          <DialogFooterV2 className="flex items-center justify-between">
            <span className="text-fg-subtle text-sm">
              Showing {showingFrom}-{showingTo} of {data.total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <CaretLeft />
                Previous
              </Button>
              <span className="text-fg-subtle text-sm px-2">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="neutral"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
                <CaretRight />
              </Button>
            </div>
          </DialogFooterV2>
        )}
      </DialogContentV2>
    </DialogRoot>
  )
}
